<script setup lang="ts">
import { BaseInput, Button, PortalIcon, useTranslation, isLocaleWith12HourFormat } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { ref, computed } from 'vue'
import InputError from '@/common/components/InputError.vue'
import { StoreToPortalMap, getDifferenceInDays } from '@/common/utils'
import EventDetails from '@/features/eventDetails/components/EventDetails.vue'
import PortalBackendLink from '@/features/eventDetails/components/PortalBackendLink.vue'
import ProductOptions from '@/features/eventDetails/components/ProductOptions.vue'
import ProductSearch from '@/features/eventDetails/components/ProductSearch.vue'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import { useModalStore } from '@/features/modal/useModalStore'
import SidePanelContent from '@/features/sidePanel/components/SidePanelContent.vue'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'
import T from '@/translations/T.vue'
import { EventType } from '@/types'

const sidebarStore = useSidebarStore()
const {
	isUpdateEvent,
	isReadonly,
	isSaving,
	store,
	name,
	products,
	type,
	startDate,
	endDate,
	isEventTooLong,
	isEventInThePast,
	isFromStoreOpportunity,
	sourceOpportunity,
	sourceOpportunityDescription,
	isScraped
} = storeToRefs(useEventDetailsStore())
const { reset, submit } = useEventDetailsStore()
const { openDeleteEventConfirmationModal } = useModalStore()
const { trackEvent } = useTelemetry()
const { hasDiscountSettersEnabled } = useFeatureFlagStore()

const { t } = useTranslation()

const productsWithIssues = ref<Set<string>>(new Set())

const MAX_EVENT_NAME_LENGTH = 80
const isEventNameTooLong = computed(() => {
	return name.value && name.value.length > MAX_EVENT_NAME_LENGTH
})
const isEventNameValid = computed(() => {
	return !isEventNameTooLong.value
})

// Check if current browser locale indicates that 12-hour format is used
// If yes then use wider panel width to accommodate time inputs with AM/PM suffix
const panelWidthClass = computed(() => (isLocaleWith12HourFormat() ? 'w-[37rem]' : 'w-[32rem]'))

const formSubmit = () => {
	if (canSubmit.value) {
		submit()
	}
}

const cancelClicked = () => {
	trackEvent('user', isUpdateEvent.value ? 'Cancelled editting an event.' : 'Cancelled adding an event.', {
		store: store.value?.displayName
	})

	reset()
	sidebarStore.closeEventSidebar()
}

const deleteEvent = () => {
	trackEvent('user', 'Started deleting an event.', { store: store.value?.displayName })

	openDeleteEventConfirmationModal()
}

const canSubmit = computed(() => {
	return (
		!isReadonly.value &&
		products.value.length > 0 &&
		productsWithIssues.value.size === 0 &&
		!isEventTooLong.value &&
		!isEventInThePast.value &&
		isEventNameValid.value
	)
})

function save() {
	if (isSaving.value) {
		return
	}

	trackEvent('user', isUpdateEvent.value ? 'Updated an event' : 'Saved an event.', {
		store: store.value?.displayName,
		startDate: startDate.value?.toISO() ?? undefined,
		endDate: endDate.value?.toISO() ?? undefined,
		name: name.value,
		productCount: products.value.length,
		eventType: type.value,
		durationInDays: (startDate.value && endDate.value && getDifferenceInDays(startDate.value, endDate.value)) ?? -1,
		discountDepths: products.value.map((product) => product.discountDepth)
	})

	submit()
}

const portal = computed(() => store.value?.id && StoreToPortalMap.get(store.value?.id))

const isDeleteButtonVisible = computed(() => isUpdateEvent.value && type.value !== EventType.Opportunity)
</script>

<template>
	<SidePanelContent :class="panelWidthClass">
		<template #heading>
			<div class="flex items-center gap-04">
				<PortalIcon v-if="portal" :portal="portal" class="portalIcon" />

				<div class="flex items-center">
					<span v-if="isUpdateEvent">{{ t('upsertEventForm.edit') }}&nbsp;</span>
					<span v-else-if="!isReadonly">{{ t('upsertEventForm.add') }}&nbsp;</span>
					<span v-else>{{ t('upsertEventForm.view') }}&nbsp;</span>

					<span v-if="store">{{ store?.displayName }}&nbsp;</span>

					{{ t('upsertEventForm.event') }}
				</div>
			</div>
		</template>

		<form class="grid h-full grid-rows-[auto_auto_1fr]" @submit.prevent @keydown.enter="formSubmit">
			<div class="w-full">
				<BaseInput
					v-model="name"
					:readonly="isReadonly"
					label="Event name"
					placeholder="Enter your custom event name"
					@focus="trackEvent('user', 'Side panel | Clicked on event name input.', { store: store?.displayName })"
				/>
				<InputError v-if="isEventNameTooLong">Event name should not exceed {{ MAX_EVENT_NAME_LENGTH }} characters.</InputError>

				<div v-if="isFromStoreOpportunity && sourceOpportunity && sourceOpportunityDescription" class="font-caption mt-03 text-ink-60">
					{{ t('upsertEventForm.storeEvent') }}: {{ sourceOpportunity.name }} ({{ sourceOpportunityDescription }})
				</div>
			</div>

			<EventDetails class="mt-06" :readonly="isReadonly" />

			<div class="mt-06 grid grid-rows-[auto_auto_auto_1fr]">
				<template v-if="!isReadonly">
					<div class="font-body-l-bold">Product(s)</div>

					<ProductSearch v-if="!isUpdateEvent" class="mt-03" />
				</template>

				<div class="mt-03 overflow-x-hidden">
					<ProductOptions @invalid-products="(event) => (productsWithIssues = event)" />
				</div>
			</div>
		</form>

		<template #pre-footer>
			<div class="note font-caption flex h-7 items-center justify-center bg-message-warning text-ink-inverted-87">
				<div v-if="isReadonly && isScraped">
					<T path="readonlyModeForScrapedEvent">
						<template #portalBackend>
							<PortalBackendLink :store-name="store?.id" />
						</template>
					</T>
				</div>

				<template v-else-if="isReadonly && !isScraped">
					{{ t('readonlyModeForPlannedEvent') }}
				</template>

				<div v-else-if="hasDiscountSettersEnabled">
					<T path="noStoreBackendActionNoteWithSettersEnabled">
						<template #portalBackend>
							<PortalBackendLink :store-name="store?.id" />
						</template>

						<template #submitDiscounts>
							<span class="font-bold">
								{{ t('submitDiscounts') }}
							</span>
						</template>
					</T>
				</div>

				<div v-else>
					<T path="noStoreBackendActionNote">
						<template #portalBackend>
							<PortalBackendLink :store-name="store?.id" />
						</template>
					</T>
				</div>
			</div>
		</template>

		<template #footer>
			<div :class="`flex items-center ${isDeleteButtonVisible ? 'justify-between' : 'justify-end'}`">
				<Button v-if="isDeleteButtonVisible" variant="tertiary" icon="delete" @click="deleteEvent">Delete</Button>

				<div class="flex items-center gap-2">
					<Button variant="tertiary" :disabled="isSaving" @click="cancelClicked">
						<template v-if="isReadonly">Close</template>
						<template v-else>Cancel</template>
					</Button>

					<Button v-if="!isReadonly" variant="primary" :loading="isSaving" :disabled="!canSubmit" @click="save">Save</Button>
				</div>
			</div>
		</template>
	</SidePanelContent>
</template>

<style scoped lang="scss">
.portalIcon {
	width: 1.5rem;
	height: 1.5rem;
}
</style>
